<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier */
import { computed, ref, watch } from 'vue';
import GamificationAPI from 'dashboard/api/gamification';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  module: { type: String, required: true },
  screen: { type: String, required: true },
  preview: { type: Boolean, default: false },
});

const currentUser = useMapGetter('getCurrentUser');
const profile = ref(null);
let profileRequest = 0;

const responsePayload = response => response?.data?.data ?? response?.data ?? null;
const numericLabel = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';

  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 0,
  }).format(numericValue);
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toUpperCase();
const profileUserId = computed(() => currentUser.value?.id || '');
const avatarUrl = computed(
  () => profile.value?.avatar_url || currentUser.value?.avatar_url || ''
);
const avatarAlt = computed(
  () => profile.value?.name || currentUser.value?.name || 'Usuário'
);
const avatarInitials = computed(() => initials(avatarAlt.value));

const loadProfile = async ([preview, userId]) => {
  profileRequest += 1;
  const request = profileRequest;

  if (preview || !userId) {
    profile.value = null;
    return;
  }

  try {
    const response = await GamificationAPI.profile(userId);
    if (request === profileRequest) profile.value = responsePayload(response);
  } catch {
    if (request === profileRequest) profile.value = null;
  }
};

watch([() => props.preview, profileUserId], loadProfile, { immediate: true });

const compact = computed(() => props.module === 'proposals');
const isProposalEdit = computed(
  () => props.module === 'proposals' && props.screen === 'edit'
);
const isVisit = computed(() => props.module === 'visits');
const isFinance = computed(() => props.module === 'finance');
const isBrokerCreate = computed(
  () => props.module === 'brokers' && props.screen === 'create'
);
const isBrokerSocial = computed(
  () => props.module === 'brokers' && props.screen !== 'create'
);
const isSales = computed(() => props.module === 'sales');
const floating = computed(
  () => compact.value && ['dashboard', 'create'].includes(props.screen)
);
const createFloating = computed(
  () => compact.value && props.screen === 'create'
);
const hasMission = computed(
  () =>
    props.preview && props.module === 'postSale' && props.screen === 'detail'
);
const gamification = computed(() =>
  props.preview
    ? {
        level: 'Nível 8 · Especialista',
        levelTitle: 'Nível 8',
        levelRole: 'Especialista',
        points: '2.450',
        pointsWithUnit: '2.450 pontos',
        progress: '72%',
        progressWidth: 'w-[72%]',
        streak: '12 dias',
      }
    : {
        level: profile.value?.name || currentUser.value?.name || 'Perfil indisponível',
        levelTitle: profile.value?.name || currentUser.value?.name || 'Perfil',
        levelRole: profile.value ? 'Pontos reais' : 'Dados indisponíveis',
        points: numericLabel(profile.value?.points),
        pointsWithUnit: Number.isFinite(Number(profile.value?.points))
          ? `${numericLabel(profile.value?.points)} pontos`
          : '—',
        pointsUnit: Number.isFinite(Number(profile.value?.points))
          ? 'pontos'
          : 'indisponíveis',
        progress: '—',
        progressWidth: 'w-0',
        streak: Number.isFinite(Number(profile.value?.entries_count))
          ? `${numericLabel(profile.value.entries_count)} entradas`
          : '—',
      }
);
const moduleIcon = computed(() => {
  if (props.module === 'finance') return 'i-lucide-wallet-cards';
  if (props.module === 'postSale') return 'i-lucide-badge-check';
  if (props.module === 'proposals') return 'i-lucide-calendar-days';
  if (props.module === 'visits') return 'i-lucide-calendar-heart';
  return 'i-lucide-shield-check';
});
</script>

<template>
  <header
    v-if="isSales"
    class="flex h-[83px] items-center border-b border-[#eeeaf5] bg-white px-5 text-[#101958]"
  >
    <section
      class="flex h-full items-center gap-4 border-r border-[#eeeaf5] pr-5"
    >
      <span
        class="grid size-10 place-items-center rounded-full bg-gradient-to-br from-[#f7c24e] via-[#e65ca8] to-[#7131d2] text-[18px] text-white shadow-[0_3px_10px_rgba(105,38,191,0.2)]"
        >★</span><span><strong class="block text-[13px]">{{ gamification.level }}</strong><i
          class="mt-2 block h-1.5 w-[142px] overflow-hidden rounded-full bg-[#ece8ef]"
          ><i
            class="block h-full rounded-full bg-gradient-to-r from-[#4c1cc2] to-[#9b34e2]"
            :class="gamification.progressWidth"
/></i></span><b class="self-end pb-1 text-[10px] text-[#4b5681]">{{ gamification.progress }}</b>
    </section>
    <section
      class="flex h-full items-center gap-3 border-r border-[#eeeaf5] px-7"
    >
      <span class="text-[24px]">🔥</span><span><small class="block text-[9px] text-[#65719a]">Sequência</small><strong class="block text-[13px]">{{ gamification.streak }}</strong></span>
    </section>
    <section class="flex h-full items-center gap-3 px-7">
      <span
        class="grid size-9 place-items-center rounded-full bg-[#f1ecff] text-[#5725ca]"
        ><span class="i-lucide-badge-plus size-5" /></span><span><small class="block text-[9px] text-[#65719a]">Saldo disponível</small><strong class="block text-[13px]">{{ gamification.pointsWithUnit }}</strong></span>
    </section>
    <button
      type="button"
      class="ml-4 flex items-center gap-2 text-[11px] font-bold text-[#3d17bf]"
    >
      <span class="i-lucide-gift size-5" />Prêmios
    </button>
    <div class="ml-auto flex items-center gap-4">
      <button type="button" class="text-[11px] font-semibold text-[#3d17bf]">
        Regras dos pontos · ⓘ
</button><span class="i-lucide-bell size-5 text-[#25376f]" /><img
        v-if="avatarUrl"
        :src="avatarUrl"
        :alt="avatarAlt"
        class="size-10 rounded-full object-cover ring-2 ring-[#d631b3] ring-offset-2"
      /><span
        v-else
        class="grid size-10 place-items-center rounded-full bg-[#f1ecff] text-[#5725ca] ring-2 ring-[#d631b3] ring-offset-2"
        >{{ avatarInitials }}</span>
    </div>
  </header>
  <header
    v-else-if="isBrokerSocial"
    class="flex h-[83px] items-center bg-white pl-[30px] pr-[30px] text-[#101958]"
  >
    <section
      class="flex h-[58px] w-[904px] items-center rounded-2xl border border-[#ebe7f2] bg-white px-5 shadow-[0_3px_14px_rgba(47,29,91,0.045)]"
    >
      <div
        class="flex min-w-[250px] items-center gap-3 border-r border-[#edeaf3] pr-5"
      >
        <span
          class="grid size-8 place-items-center rounded-full bg-[#f2ecff] text-[#5c27ce]"
          ><span class="i-lucide-sparkles size-4" /></span><span><b class="block text-[12px]">{{ gamification.level }}</b><i
            class="mt-2 block h-1.5 w-[180px] overflow-hidden rounded-full bg-[#e6e3eb]"
            ><i
              class="block h-full rounded-full bg-gradient-to-r from-[#4b1bbf] to-[#9b38df]"
              :class="gamification.progressWidth"
/></i></span><small class="self-end pb-1 text-[9px]">{{ gamification.progress }}</small>
      </div>
      <div class="flex items-center gap-3 border-r border-[#edeaf3] px-5">
        <span class="text-[20px]">🔥</span><span><small class="block text-[8px] text-[#657198]">Sequência</small><b class="block text-[11px]">{{ gamification.streak }}</b></span>
      </div>
      <div class="flex items-center gap-3 px-5">
        <span
          class="grid size-8 place-items-center rounded-full bg-[#f2ecff] text-[#5c27ce]"
          ><span class="i-lucide-badge-plus size-4" /></span><span><small class="block text-[8px] text-[#657198]">Pontos</small><b class="block text-[13px]">{{ gamification.points }}</b></span>
      </div>
      <button
        type="button"
        class="ml-auto flex items-center gap-2 text-[10px] font-semibold"
      >
        <span class="i-lucide-gift size-4 text-[#5c27ce]" />Prêmios
</button><button
        type="button"
        class="ml-6 flex items-center gap-2 text-[10px] font-semibold"
      >
        <span class="i-lucide-shield-check size-4 text-[#5c27ce]" />Regras dos
        pontos
      </button>
    </section>
    <div class="ml-auto flex items-center gap-4">
      <button
        type="button"
        class="i-lucide-bell size-6 text-[#27376d]"
      /><button
        v-if="props.screen === 'detail'"
        type="button"
        class="flex h-11 items-center gap-3 rounded-xl bg-gradient-to-r from-[#5920ce] to-[#31c8e7] px-5 text-[10px] font-bold text-white"
      >
        <span class="i-lucide-share-2 size-4" />Compartilhar perfil
</button><button
        type="button"
        class="grid size-11 place-items-center rounded-xl bg-white text-[#2a3970] shadow-[0_3px_12px_rgba(47,29,91,0.08)]"
      >
        <span class="i-lucide-ellipsis size-5" />
      </button>
    </div>
  </header>
  <header
    v-else-if="isBrokerCreate"
    class="flex h-[77px] items-center bg-white px-5 text-[#101958]"
  >
    <label
      class="flex h-[50px] w-[353px] items-center gap-3 rounded-xl border border-[#e7e3ed] px-4 text-[11px] text-[#7c85a1] shadow-[0_3px_12px_rgba(45,28,92,0.04)]"
      ><span class="i-lucide-search size-4" /><input
        class="w-full border-0 bg-transparent p-0 text-[11px] outline-none"
        placeholder="Buscar pessoas, imóveis ou publicações"
    /></label>
    <div class="ml-auto flex h-full items-center gap-5">
      <div class="flex items-center gap-3 border-r border-[#eeeaf4] pr-5">
        <span><b class="block text-[12px]">{{ gamification.level }}</b><i
            class="mt-2 block h-1.5 w-[145px] overflow-hidden rounded-full bg-[#e8e6ed]"
            ><i
              class="block h-full rounded-full bg-gradient-to-r from-[#4e1fc4] to-[#9631df]"
              :class="gamification.progressWidth"
/></i></span><small class="self-end pb-1 text-[9px]">{{ gamification.progress }}</small>
      </div>
      <div class="flex items-center gap-3 border-r border-[#eeeaf4] pr-5">
        <span class="text-[21px]">🔥</span><span><small class="block text-[9px] text-[#66739a]">Sequência</small><strong class="block text-[13px]">{{ gamification.streak }}</strong></span>
      </div>
      <div class="flex items-center gap-3">
        <span
          class="grid size-8 place-items-center rounded-full bg-[#f1ecff] text-[#5e2ccc]"
          ><span class="i-lucide-sparkles size-4" /></span><span><small class="block text-[9px] text-[#66739a]">Pontos</small><strong class="block text-[13px]">{{ gamification.points }}</strong></span>
      </div>
      <button
        type="button"
        class="flex h-10 items-center gap-2 rounded-xl border border-[#e7e3ed] px-4 text-[10px] font-bold"
      >
        <span class="i-lucide-gift size-4 text-[#5620c8]" />Prêmios
</button><button
        type="button"
        class="flex h-10 items-center gap-2 rounded-xl border border-[#e7e3ed] px-4 text-[10px] font-bold"
      >
        <span class="i-lucide-circle-help size-4" />Regras dos pontos
</button><span class="i-lucide-bell size-5 text-[#25376f]" /><img
        v-if="avatarUrl"
        :src="avatarUrl"
        :alt="avatarAlt"
        class="size-11 rounded-full object-cover ring-2 ring-[#6944db] ring-offset-2"
      /><span
        v-else
        class="grid size-11 place-items-center rounded-full bg-[#f1ecff] text-[#5e2ccc] ring-2 ring-[#6944db] ring-offset-2"
        >{{ avatarInitials }}</span>
    </div>
  </header>
  <header
    v-else-if="isFinance"
    class="relative h-[15px] text-[#101958]"
  >
    <section
      class="absolute right-6 top-3 z-20 flex h-[72px] w-[615px] items-center rounded-2xl border border-[#ebe7f2] bg-white px-4 shadow-[0_3px_16px_rgba(47,29,91,0.05)]"
    >
      <div class="flex w-full items-center">
        <div
          class="flex min-w-[210px] items-center gap-3 border-r border-[#ece8f2] pr-4"
        >
          <span
            class="grid size-10 place-items-center rounded-full bg-gradient-to-br from-[#3816a9] to-[#8f2dde] text-white shadow-[0_4px_14px_rgba(79,29,180,0.25)]"
            ><span class="i-lucide-circle-dollar-sign size-5" /></span>
          <span class="min-w-0 flex-1"><b class="block text-[11px]">{{ gamification.levelTitle }}</b><strong class="mt-0.5 block text-[9px]">{{ gamification.levelRole }}</strong><i
              class="mt-1 block h-1.5 overflow-hidden rounded-full bg-[#eae8ee]"
              ><i
                class="block h-full rounded-full bg-gradient-to-r from-[#4a19be] to-[#8528dd]"
                :class="gamification.progressWidth"
/></i></span><small class="self-end pb-1 text-[8px]">{{ gamification.progress }}</small>
        </div>
        <div
          class="flex min-w-[112px] items-center gap-2 border-r border-[#ece8f2] px-4"
        >
          <span class="text-[20px]">🔥</span><span><small class="block text-[8px]">Sequência</small><strong class="block text-[11px]">{{ gamification.streak }}</strong></span>
        </div>
        <div class="flex min-w-[108px] items-center gap-2 px-4">
          <span class="i-lucide-gem size-5 text-[#5520ca]" /><span><strong class="block text-[13px] leading-none">{{ gamification.points }}</strong><small class="mt-1 block text-[8px]">{{ gamification.pointsUnit }}</small></span>
        </div>
        <div class="ml-auto flex gap-2">
          <button
            type="button"
            class="flex h-9 items-center gap-1.5 rounded-lg border border-[#ded8ec] px-3 text-[9px] font-semibold text-[#33206c]"
          >
            <span class="i-lucide-gift size-3.5 text-[#5622c9]" />Prêmios
</button><button
            type="button"
            class="flex h-9 items-center gap-1.5 rounded-lg border border-[#ded8ec] px-3 text-[9px] font-semibold text-[#33206c]"
          >
            <span class="i-lucide-circle-help size-3.5 text-[#5622c9]" />Regras
            dos pontos
          </button>
        </div>
      </div>
    </section>
  </header>
  <header
    v-else-if="isVisit"
    class="flex h-[91px] items-center border-b border-[#eeeaf5] bg-white px-6 text-[#101958]"
  >
    <section
      class="flex h-full items-center gap-4 border-r border-[#eeeaf5] pr-5"
    >
      <span
        class="grid size-10 place-items-center rounded-full bg-gradient-to-br from-[#ffcc4c] via-[#ff758f] to-[#6b28d7] text-[19px] text-white shadow-[0_3px_10px_rgba(114,42,204,0.18)]"
        >★</span>
      <span>
        <strong class="block text-[13px]">{{ gamification.level }}</strong>
        <i
          class="mt-2 block h-1.5 w-[110px] overflow-hidden rounded-full bg-[#ece8ef]"
          ><i
            class="block h-full rounded-full bg-gradient-to-r from-[#4c1cc2] to-[#a33ce5]"
            :class="gamification.progressWidth"
        /></i>
      </span>
      <b class="self-start pt-6 text-[10px] text-[#4b5681]">{{ gamification.progress }}</b>
    </section>
    <section
      class="flex h-full items-center gap-3 border-r border-[#eeeaf5] px-7"
    >
      <span class="text-[25px]">🔥</span><span><small class="block text-[10px] text-[#65719a]">Sequência</small><strong class="block text-[13px]">{{ gamification.streak }}</strong></span>
    </section>
    <section class="flex h-full items-center gap-3 px-7">
      <span
        class="grid size-9 place-items-center rounded-full bg-[#f1ecff] text-[#5a25ca]"
        ><span class="i-lucide-badge-plus size-5" /></span><span><small class="block text-[10px] text-[#65719a]">Carteira</small><strong class="block text-[13px]">{{ gamification.pointsWithUnit }}</strong></span>
    </section>
    <div class="ml-auto flex items-center gap-4">
      <button
        type="button"
        class="flex h-10 min-w-[112px] items-center justify-center gap-3 rounded-xl border border-[#eeeaf5] bg-white px-4 text-[11px] font-bold text-[#3e19bd]"
      >
        <span class="i-lucide-gift size-5" /> Prêmios
      </button>
      <button
        type="button"
        class="flex h-10 min-w-[170px] items-center justify-center gap-3 rounded-xl border border-[#eeeaf5] bg-white px-4 text-[11px] font-bold text-[#3e19bd]"
      >
        <span class="i-lucide-circle-help size-5" /> Regras dos pontos
      </button>
      <img
        v-if="avatarUrl"
        :src="avatarUrl"
        :alt="avatarAlt"
        class="size-11 rounded-full object-cover ring-2 ring-[#d92fb8] ring-offset-2"
      /><span
        v-else
        class="grid size-11 place-items-center rounded-full bg-[#f1ecff] text-[#5a25ca] ring-2 ring-[#d92fb8] ring-offset-2"
        >{{ avatarInitials }}</span>
      <span class="i-lucide-chevron-down size-4 text-[#3e4d78]" />
    </div>
  </header>
  <div
    v-else-if="isProposalEdit"
    data-imudei-visual-id="proposal-edit-header"
    class="pointer-events-none absolute left-5 right-5 top-3 z-20 h-[158px]"
  >
    <header
      class="pointer-events-auto absolute -left-5 -right-5 -top-3 flex h-[83px] items-center border-b border-[#eeeaf6] bg-white px-4 text-[#101958]"
    >
      <span
        class="grid size-16 shrink-0 place-items-center rounded-xl border border-[#ddd0ff] bg-[#faf7ff] text-[#5725ca] shadow-[0_3px_14px_rgba(44,28,98,0.04)]"
      >
        <span class="i-lucide-sparkles size-8" />
      </span>

      <section
        class="ml-[34px] flex h-16 w-[326px] shrink-0 items-center gap-3 rounded-xl border border-[#eeeaf6] bg-white px-4 shadow-[0_3px_16px_rgba(44,28,98,0.05)]"
      >
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="avatarAlt"
          class="size-10 rounded-full object-cover ring-2 ring-[#d92fb8] ring-offset-2"
        />
        <span
          v-else
          class="grid size-10 place-items-center rounded-full bg-[#f1ecff] text-[10px] font-bold text-[#5a25ca] ring-2 ring-[#d92fb8] ring-offset-2"
        >
          {{ avatarInitials }}
        </span>
        <span class="min-w-0 flex-1">
          <strong class="block truncate text-[12px] text-[#0d1958]">{{ gamification.level }}</strong>
          <span class="mt-2 block h-1.5 overflow-hidden rounded-full bg-[#e8e8ee]">
            <span
              class="block h-full rounded-full bg-gradient-to-r from-[#5120d0] to-[#932ee2]"
              :class="gamification.progressWidth"
            />
          </span>
        </span>
        <small class="self-end pb-2 text-[10px] font-medium text-[#59658d]">
          {{ gamification.progress }}
        </small>
      </section>

      <section
        class="ml-5 flex h-16 w-[136px] shrink-0 items-center gap-3 rounded-xl border border-[#eeeaf6] bg-white px-4 shadow-[0_3px_16px_rgba(44,28,98,0.05)]"
      >
        <span class="grid size-8 place-items-center rounded-full bg-[#fff4e9] text-[#ff7418]">
          <span class="i-lucide-flame size-5" />
        </span>
        <span>
          <small class="block text-[9px] text-[#65719b]">Sequência</small>
          <strong class="block text-[13px] text-[#101956]">{{ gamification.streak }}</strong>
        </span>
      </section>

      <section
        class="ml-3 flex h-16 w-[122px] shrink-0 items-center gap-3 rounded-xl border border-[#eeeaf6] bg-white px-4 shadow-[0_3px_16px_rgba(44,28,98,0.05)]"
      >
        <span class="grid size-8 place-items-center rounded-full bg-[#f0ecff] text-[#5625ca]">
          <span class="i-lucide-sparkles size-4" />
        </span>
        <span>
          <small class="block text-[9px] text-[#65719b]">Pontos</small>
          <strong class="block text-[13px] text-[#101956]">{{ gamification.points }}</strong>
        </span>
      </section>

      <button
        type="button"
        class="ml-4 flex h-16 w-[114px] shrink-0 items-center justify-center gap-2 rounded-xl border border-[#eeeaf6] bg-white px-3 text-[11px] font-bold text-[#d92c8d] shadow-[0_3px_16px_rgba(44,28,98,0.05)]"
      >
        <span class="i-lucide-gift size-5 text-[#ff654b]" /> Prêmios
      </button>
      <button
        type="button"
        class="ml-5 flex shrink-0 items-center gap-2 text-[10px] font-semibold text-[#264cc5]"
      >
        Regras dos pontos <span class="i-lucide-circle-help size-4 text-[#69769d]" />
      </button>

      <div class="ml-auto flex items-center gap-3">
        <img
          v-if="avatarUrl"
          :src="avatarUrl"
          :alt="avatarAlt"
          class="size-10 rounded-full object-cover ring-2 ring-[#d92fb8] ring-offset-2"
        />
        <span
          v-else
          class="grid size-10 place-items-center rounded-full bg-[#f1ecff] text-[10px] font-bold text-[#5a25ca] ring-2 ring-[#d92fb8] ring-offset-2"
        >
          {{ avatarInitials }}
        </span>
        <span
          class="grid size-10 place-items-center rounded-full border border-[#ded8f8] bg-[#faf9ff] text-[10px] font-semibold text-[#4d38af]"
          title="A API atual não disponibiliza a quantidade de participantes nesta tela."
        >
          —
        </span>
      </div>
    </header>
  </div>
  <header
    v-else
    class="border-b border-[#eeeaf6] bg-white px-6 py-3"
    :class="
      floating
        ? [
            'absolute right-0 top-0 z-20 border-b-0 bg-transparent px-4 py-3',
            createFloating ? 'w-[52%]' : 'w-[68%]',
          ]
        : ''
    "
  >
    <div
      class="mx-auto flex min-h-[58px] w-full items-center overflow-hidden rounded-2xl border border-[#f0edf6] bg-white shadow-[0_3px_16px_rgba(44,28,98,0.05)]"
      :class="[
        compact ? 'max-w-[1320px]' : '',
        floating ? 'min-h-[56px] shadow-[0_3px_12px_rgba(44,28,98,0.04)]' : '',
      ]"
    >
      <div class="flex min-w-[255px] items-center gap-3 px-5">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#4120bc] to-[#8b2fd7] text-white shadow-[0_4px_14px_rgba(76,35,187,0.3)]"
        >
          <span :class="moduleIcon" class="size-5" />
        </span>
        <div class="min-w-[160px]">
          <div class="flex items-center justify-between gap-3 text-[12px]">
            <strong class="text-[#0d1958]">{{ gamification.level }}</strong>
            <span class="text-[#59658d]">{{ gamification.progress }}</span>
          </div>
          <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-[#e8e8ee]">
            <span
              class="block h-full rounded-full bg-gradient-to-r from-[#5120d0] to-[#932ee2]"
              :class="gamification.progressWidth"
            />
          </div>
        </div>
      </div>

      <div
        class="flex min-w-[148px] items-center gap-3 border-l border-[#f0edf6] px-5"
      >
        <span
          class="grid size-8 place-items-center rounded-full bg-[#fff4e9] text-[#ff7418]"
        >
          <span class="i-lucide-flame size-5" />
        </span>
        <span>
          <small class="block text-[9px] text-[#65719b]">Sequência</small>
          <strong class="block text-[13px] text-[#101956]">{{ gamification.streak }}</strong>
        </span>
      </div>

      <div
        class="flex min-w-[150px] items-center gap-3 border-l border-[#f0edf6] px-5"
      >
        <span
          class="grid size-8 place-items-center rounded-full bg-[#f0ecff] text-[#5625ca]"
        >
          <span class="i-lucide-sparkles size-4" />
        </span>
        <span>
          <small class="block text-[9px] text-[#65719b]">Pontos</small>
          <strong class="block text-[13px] text-[#101956]">{{ gamification.points }}
            <em class="not-italic text-[10px] font-medium">{{ gamification.pointsUnit || 'pontos' }}</em></strong>
        </span>
      </div>

      <button
        class="flex min-w-[126px] items-center gap-2 border-l border-[#f0edf6] px-5 text-left text-[11px] font-bold text-[#3d17bf]"
      >
        <span
          class="grid size-8 place-items-center rounded-lg bg-[#faf7ff] text-[#5625ca]"
          ><span class="i-lucide-gift size-4" /></span>
        Prêmios
      </button>

      <div
        v-if="hasMission"
        class="mx-3 flex min-w-[310px] flex-1 items-center gap-3 rounded-xl bg-gradient-to-r from-[#f4efff] to-[#fbf9ff] px-4 py-2"
      >
        <span
          class="grid size-8 place-items-center rounded-full bg-white text-[#6330cf] shadow-sm"
          ><span class="i-lucide-target size-4" /></span>
        <span class="min-w-0 flex-1">
          <strong class="block truncate text-[10px] text-[#17195d]">Como você ganha pontos</strong>
          <small class="block truncate text-[9px] text-[#66719a]">Tarefa +25 · Doc. aprovado +30 · Etapa +60</small>
        </span>
        <span
          class="rounded-full bg-white px-2.5 py-1 text-[9px] font-bold text-[#5520c5]"
          >+180 pts</span>
      </div>

      <button
        v-if="!hasMission"
        class="ml-auto flex min-w-[154px] items-center justify-center gap-2 border-l border-[#f0edf6] px-5 text-[10px] font-semibold text-[#192466]"
      >
        <span class="i-lucide-circle-help size-4 text-[#69769d]" />
        Regras dos pontos
      </button>
    </div>
  </header>
</template>
